import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { toast } from "sonner";
import {
  Users, Package, Activity, ShieldCheck, Trash2, Power, PowerOff,
  RefreshCw, Search, Crown, Settings2, Upload, RotateCcw, Palette,
  Type, Image, Eye, Save, Globe, Pencil, Mail, Building2, User,
  Lock, X, ToggleLeft, ToggleRight, Store, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Clock, UserPlus, BarChart3, Zap,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { JasrBrand, JasrMark } from "@/components/JasrLogo";

const STATUS_MAP = {
  success: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "نجح" },
  failed:  { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     label: "فشل" },
  partial: { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500",   label: "جزئي" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { bg: "bg-slate-100", text: "text-[var(--text-secondary)]", dot: "bg-slate-400", label: "—" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function RoleBadge({ role }) {
  return role === "admin" ? (
    <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-[#004EEB]/10 text-[#004EEB] border border-[#004EEB]/20">
      <Crown className="w-3 h-3" /> مدير
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-[var(--text-secondary)]">
      مستأجر
    </span>
  );
}

function TenantAvatar({ name }) {
  const ch = (name || "م").slice(0, 1);
  const colors = ["bg-[#004EEB]", "bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-purple-500"];
  const idx = ch.charCodeAt(0) % colors.length;
  return (
    <div className={`w-9 h-9 rounded-xl ${colors[idx]} grid place-items-center text-white font-bold text-sm shrink-0`}>
      {ch}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone = "blue" }) {
  const tones = {
    blue:   { bar: "bg-[#004EEB]", icon: "text-[#004EEB] bg-[#004EEB]/8" },
    emerald:{ bar: "bg-emerald-500", icon: "text-emerald-600 bg-emerald-50" },
    indigo: { bar: "bg-indigo-500", icon: "text-indigo-600 bg-indigo-50" },
    amber:  { bar: "bg-amber-500", icon: "text-amber-600 bg-amber-50" },
  };
  const t = tones[tone];
  return (
    <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 p-5 relative overflow-hidden card-hover">
      <div className={`absolute top-0 inset-x-0 h-[3px] ${t.bar}`} />
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-semibold text-[var(--text-secondary)]">{label}</span>
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${t.icon} shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-3xl font-bold mono text-[var(--text-primary)]">{value ?? "—"}</div>
    </div>
  );
}

/* ════════════════ Tenant Edit Sheet ════════════════ */
function TenantEditSheet({ tenant, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || "",
        email: tenant.email || "",
        company_name: tenant.company_name || "",
        role: tenant.role || "tenant",
        disabled: !!tenant.disabled,
      });
      setNewPassword("");
    }
  }, [tenant]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("الاسم مطلوب"); return; }
    if (!form.email?.trim()) { toast.error("البريد الإلكتروني مطلوب"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (newPassword) body.password = newPassword;
      const { data } = await api.patch(`/admin/tenants/${tenant.id}`, body);
      toast.success("تم حفظ بيانات المستأجر");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!tenant) return null;

  const colors = ["bg-[#004EEB]", "bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-purple-500"];
  const ch = (tenant.name || "م").slice(0, 1);
  const avatarColor = colors[ch.charCodeAt(0) % colors.length];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="left"
        dir="rtl"
        className="w-full sm:max-w-[480px] p-0 flex flex-col bg-[#F8FAFC] border-e border-[var(--border-color)]"
      >
        {/* Header */}
        <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-6 py-5 flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl ${avatarColor} grid place-items-center text-white font-bold text-lg shrink-0`}>
            {ch}
          </div>
          <div className="flex-1 min-w-0">
            <SheetTitle className="text-[var(--text-primary)] font-bold text-base leading-tight truncate">
              {tenant.name}
            </SheetTitle>
            <SheetDescription className="text-[var(--text-muted)] text-xs mono truncate mt-0.5">
              {tenant.email}
            </SheetDescription>
            <div className="flex items-center gap-2 mt-2">
              <RoleBadge role={tenant.role} />
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                tenant.disabled ? "bg-slate-100 text-[var(--text-secondary)]" : "bg-emerald-50 text-emerald-700"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${tenant.disabled ? "bg-slate-400" : "bg-emerald-500"}`} />
                {tenant.disabled ? "معطَّل" : "نشط"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:bg-slate-100 hover:text-[var(--text-secondary)] transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Identity */}
          <section>
            <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-[var(--text-muted)] mb-3">البيانات الأساسية</p>
            <div className="space-y-3.5">
              <FieldRow icon={User} label="الاسم الكامل">
                <input
                  data-testid="edit-tenant-name"
                  value={form.name}
                  onChange={set("name")}
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-3.5 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all"
                />
              </FieldRow>
              <FieldRow icon={Mail} label="البريد الإلكتروني">
                <input
                  data-testid="edit-tenant-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-3.5 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all mono"
                />
              </FieldRow>
              <FieldRow icon={Building2} label="اسم الشركة / المتجر">
                <input
                  data-testid="edit-tenant-company"
                  value={form.company_name}
                  onChange={set("company_name")}
                  placeholder="اختياري"
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-3.5 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all"
                />
              </FieldRow>
            </div>
          </section>

          {/* Password reset */}
          <section>
            <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-[var(--text-muted)] mb-3">إعادة تعيين كلمة المرور</p>
            <FieldRow icon={Lock} label="كلمة المرور الجديدة">
              <div className="relative">
                <input
                  data-testid="edit-tenant-password"
                  type={showPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="اتركه فارغاً إذا لم ترد التغيير"
                  className="w-full bg-[var(--bg-card)] border border-[var(--border-color)] px-3.5 pe-10 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            </FieldRow>
          </section>

          {/* Role & Status */}
          <section>
            <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-[var(--text-muted)] mb-3">الصلاحيات والحالة</p>
            <div className="space-y-3">
              {/* Role */}
              <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-4">
                <p className="text-xs font-semibold text-[var(--text-secondary)] mb-3">الصلاحية</p>
                <div className="flex gap-2">
                  {[
                    { value: "tenant", label: "مستأجر", desc: "وصول عادي للمنصة" },
                    { value: "admin", label: "مدير", desc: "صلاحيات كاملة" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`edit-role-${opt.value}`}
                      onClick={() => setForm((f) => ({ ...f, role: opt.value }))}
                      className={`flex-1 p-3 rounded-xl border-2 text-start transition-all ${
                        form.role === opt.value
                          ? "border-[#004EEB] bg-[#004EEB]/5"
                          : "border-[var(--border-color)] hover:border-slate-300"
                      }`}
                    >
                      <div className={`text-sm font-bold ${form.role === opt.value ? "text-[#004EEB]" : "text-[var(--text-secondary)]"}`}>
                        {opt.label}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Status toggle */}
              <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[var(--text-secondary)]">حالة الحساب</p>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    {form.disabled ? "الحساب معطَّل — المستخدم لا يستطيع الدخول" : "الحساب نشط — يمكن تسجيل الدخول"}
                  </p>
                </div>
                <button
                  data-testid="edit-tenant-toggle-status"
                  onClick={() => setForm((f) => ({ ...f, disabled: !f.disabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${form.disabled ? "bg-slate-300" : "bg-emerald-500"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-[var(--bg-card)] rounded-full shadow transition-all ${form.disabled ? "start-0.5" : "start-6"}`} />
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-[var(--bg-card)] border-t border-[var(--border-color)] px-6 py-4 flex gap-3">
          <button
            data-testid="edit-tenant-save-btn"
            onClick={save}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 bg-[#004EEB] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#0040C1] transition-all disabled:opacity-60 shadow-lg shadow-[#004EEB]/20"
          >
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? "جارٍ الحفظ…" : "حفظ التغييرات"}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-3 border border-[var(--border-color)] text-[var(--text-secondary)] rounded-xl text-sm font-medium hover:border-slate-400 transition-colors"
          >
            إلغاء
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FieldRow({ icon: Icon, label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-[11px] tracking-[0.08em] uppercase font-semibold text-[var(--text-secondary)]">
        <Icon className="w-3 h-3" />
        {label}
      </label>
      {children}
    </div>
  );
}

/* ════════════════ Tenants Tab ════════════════ */
function TenantsTab({ user }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editTenant, setEditTenant] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tenants");
      setTenants(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleDisable = async (t) => {
    try {
      await api.patch(`/admin/tenants/${t.id}`, { disabled: !t.disabled });
      setTenants((prev) => prev.map((x) => x.id === t.id ? { ...x, disabled: !t.disabled } : x));
      toast.success(t.disabled ? "تم تفعيل الحساب" : "تم تعطيل الحساب");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTenant = async (id) => {
    try {
      await api.delete(`/admin/tenants/${id}`);
      setTenants((prev) => prev.filter((x) => x.id !== id));
      toast.success("تم حذف المستأجر");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleSaved = (updated) => {
    setTenants((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x));
  };

  const filtered = tenants.filter((t) =>
    !q || t.name?.includes(q) || t.email?.includes(q) || t.company_name?.includes(q)
  );

  const totalProducts = tenants.reduce((s, t) => s + (t.products_count || 0), 0);
  const activeCount = tenants.filter((t) => !t.disabled).length;
  const tenantOnly = tenants.filter((t) => t.role !== "admin").length;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("ar-SA-u-nu-latn", { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <div className="space-y-6">
      {/* Edit Sheet */}
      <TenantEditSheet
        tenant={editTenant}
        open={!!editTenant}
        onClose={() => setEditTenant(null)}
        onSaved={handleSaved}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        <SummaryCard label="إجمالي المستأجرين" value={tenantOnly} icon={Users} tone="blue" />
        <SummaryCard label="الحسابات النشطة" value={activeCount} icon={Activity} tone="emerald" />
        <SummaryCard label="إجمالي المنتجات" value={totalProducts.toLocaleString("en-US")} icon={Package} tone="indigo" />
        <SummaryCard label="المدراء" value={tenants.filter((t) => t.role === "admin").length} icon={Crown} tone="amber" />
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border-color)] flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث بالاسم، البريد، أو اسم الشركة…"
              data-testid="admin-search-input"
              className="w-full bg-[#F8FAFC] border border-[var(--border-color)] rounded-xl ps-10 pe-3 py-2.5 text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 focus:bg-[var(--bg-card)] transition-all"
            />
          </div>
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[var(--text-secondary)] border border-[var(--border-color)] rounded-xl hover:border-slate-400 transition-colors bg-[var(--bg-card)]">
            <RefreshCw className="w-3.5 h-3.5" /> تحديث
          </button>
          <span className="text-xs text-[var(--text-muted)] mono">{filtered.length} مستخدم</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-tenants-table">
            <thead>
              <tr className="border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/50">
                <th className="th-overline text-start px-5 py-3">المستخدم</th>
                <th className="th-overline text-start px-5 py-3">الشركة</th>
                <th className="th-overline text-start px-5 py-3">الصلاحية</th>
                <th className="th-overline text-end px-5 py-3">المنتجات</th>
                <th className="th-overline text-start px-5 py-3">آخر تزامن</th>
                <th className="th-overline text-start px-5 py-3">الحالة</th>
                <th className="th-overline text-start px-5 py-3">تاريخ الانضمام</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="py-16 text-center text-[var(--text-muted)] text-sm">
                  <RefreshCw className="w-5 h-5 animate-spin inline-block mb-1" /><br />جارٍ التحميل...
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="py-16 text-center text-[var(--text-muted)] text-sm">لا يوجد مستخدمون بعد.</td></tr>
              )}
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  data-testid="admin-tenant-row"
                  onClick={() => setEditTenant(t)}
                  className={`border-b border-slate-50 transition-colors cursor-pointer ${
                    t.disabled ? "opacity-60 bg-[var(--bg-card-hover)]/50 hover:bg-slate-100/60" : "hover:bg-blue-50/40"
                  }`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <TenantAvatar name={t.name} />
                      <div>
                        <div className="font-semibold text-[var(--text-primary)] text-sm">{t.name}</div>
                        <div className="text-[11px] text-[var(--text-muted)] mono">{t.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-[var(--text-secondary)]">{t.company_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-5 py-4"><RoleBadge role={t.role} /></td>
                  <td className="px-5 py-4 text-end"><span className="mono font-bold text-[var(--text-primary)]">{(t.products_count || 0).toLocaleString("en-US")}</span></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {t.last_sync_status && <StatusBadge status={t.last_sync_status} />}
                      <span className="text-[11px] text-[var(--text-muted)] mono">{fmt(t.last_sync)}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${t.disabled ? "bg-slate-100 text-[var(--text-secondary)]" : "bg-emerald-50 text-emerald-700"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${t.disabled ? "bg-slate-400" : "bg-emerald-500"}`} />
                      {t.disabled ? "معطَّل" : "نشط"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-[11px] text-[var(--text-muted)] mono">{fmt(t.created_at)}</td>
                  <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      {/* Edit button — always visible */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditTenant(t); }}
                        data-testid={`admin-edit-${t.id}`}
                        title="تعديل الملف"
                        className="p-2 rounded-lg text-[#004EEB] hover:bg-[#004EEB]/8 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {t.id !== user?.id && t.role !== "admin" && (
                        <>
                          <button onClick={() => toggleDisable(t)} data-testid={`admin-toggle-${t.id}`}
                            title={t.disabled ? "تفعيل" : "تعطيل"}
                            className={`p-2 rounded-lg transition-colors ${t.disabled ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
                          >
                            {t.disabled ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`admin-delete-${t.id}`} title="حذف" className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent dir="rtl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>تأكيد حذف المستأجر</AlertDialogTitle>
                                <AlertDialogDescription>
                                  سيتم حذف حساب <span className="font-bold text-[var(--text-primary)]">{t.name}</span> ({t.email}) نهائياً. هذا الإجراء لا يمكن التراجع عنه.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteTenant(t.id)} className="bg-red-600 hover:bg-red-700">حذف نهائياً</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ════════════════ Platform Settings Tab ════════════════ */
function PlatformSettingsTab() {
  const { name: ctxName, tagline: ctxTagline, logo_base64: ctxLogo, primary_color: ctxColor, reload } = usePlatform();

  const [form, setForm] = useState({ name: "", tagline: "", logo_base64: null, primary_color: "#004EEB" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Sync form with current platform settings
  useEffect(() => {
    setForm({ name: ctxName, tagline: ctxTagline, logo_base64: ctxLogo, primary_color: ctxColor || "#004EEB" });
    setPreview(ctxLogo);
  }, [ctxName, ctxTagline, ctxLogo, ctxColor]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("حجم الصورة يجب ألا يتجاوز 500 كيلوبايت"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result;
      setPreview(b64);
      setForm((f) => ({ ...f, logo_base64: b64 }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setPreview(null);
    setForm((f) => ({ ...f, logo_base64: null }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("اسم المنصة مطلوب"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/platform-settings", form);
      // Push to context immediately
      reload();
      toast.success("تم حفظ إعدادات المنصة بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetToDefault = () => {
    setForm({ name: "جسر", tagline: "جسر ذكي بين أنظمتك", logo_base64: null, primary_color: "#004EEB" });
    setPreview(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Form — 3 cols */}
      <div className="lg:col-span-3 space-y-5">

        {/* Section: Identity */}
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center gap-2">
            <Type className="w-4 h-4 text-[#004EEB]" />
            <h3 className="font-semibold text-[var(--text-primary)] text-sm">هوية المنصة</h3>
          </div>
          <div className="p-5 space-y-4">
            <label className="block space-y-1.5">
              <span className="text-[11px] tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">اسم المنصة *</span>
              <input
                data-testid="platform-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="جسر"
                className="w-full bg-[#F8FAFC] border border-[var(--border-color)] px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-[11px] tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">الشعار النصي (tagline)</span>
              <input
                data-testid="platform-tagline-input"
                value={form.tagline}
                onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
                placeholder="جسر ذكي بين أنظمتك"
                className="w-full bg-[#F8FAFC] border border-[var(--border-color)] px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all"
              />
            </label>
          </div>
        </div>

        {/* Section: Logo */}
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center gap-2">
            <Image className="w-4 h-4 text-[#004EEB]" />
            <h3 className="font-semibold text-[var(--text-primary)] text-sm">شعار المنصة (Logo)</h3>
          </div>
          <div className="p-5">
            <div className="flex items-start gap-4">
              {/* Current logo preview */}
              <div className="w-20 h-20 rounded-2xl bg-[#004EEB] grid place-items-center shrink-0 shadow-[0_0_20px_rgba(0,78,235,0.3)] overflow-hidden">
                {preview
                  ? <img src={preview} alt="logo" className="w-14 h-14 object-contain" />
                  : <JasrMark size={44} />
                }
              </div>
              <div className="flex-1 space-y-3">
                <p className="text-sm text-[var(--text-secondary)]">ارفع صورة PNG أو JPG أو SVG. الحد الأقصى 500 كيلوبايت. يظهر الشعار في القائمة الجانبية وصفحات الدخول والصفحة الرئيسية.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="platform-logo-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#004EEB] text-white rounded-xl text-sm font-semibold hover:bg-[#0040C1] transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    رفع شعار
                  </button>
                  {preview && (
                    <button
                      onClick={removeLogo}
                      data-testid="platform-logo-remove-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors border border-red-200"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      إزالة الشعار
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                  data-testid="platform-logo-file-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section: Color */}
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center gap-2">
            <Palette className="w-4 h-4 text-[#004EEB]" />
            <h3 className="font-semibold text-[var(--text-primary)] text-sm">اللون الأساسي</h3>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                  data-testid="platform-color-input"
                  className="w-14 h-14 rounded-xl border-2 border-[var(--border-color)] cursor-pointer overflow-hidden p-1"
                />
              </div>
              <div>
                <p className="font-semibold text-[var(--text-primary)] text-sm">{form.primary_color}</p>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">يُطبَّق على الأزرار والمؤشرات في المنصة</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {["#004EEB", "#7C3AED", "#059669", "#DC2626", "#D97706", "#0891B2"].map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm((f) => ({ ...f, primary_color: c }))}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${form.primary_color === c ? "border-slate-900 scale-110" : "border-transparent hover:scale-105"}`}
                    style={{ background: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            data-testid="platform-save-btn"
            onClick={save}
            disabled={busy}
            className="flex items-center gap-2 px-6 py-3 bg-[#004EEB] text-white rounded-xl font-semibold hover:bg-[#0040C1] transition-all disabled:opacity-60 shadow-lg shadow-[#004EEB]/20"
          >
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? "جارٍ الحفظ…" : "حفظ التغييرات"}
          </button>
          <button
            onClick={resetToDefault}
            className="flex items-center gap-2 px-4 py-3 border border-[var(--border-color)] text-[var(--text-secondary)] rounded-xl text-sm hover:border-slate-400 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            إعادة للافتراضي
          </button>
        </div>
      </div>

      {/* Live Preview — 2 cols */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden sticky top-6">
          <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#004EEB]" />
            <h3 className="font-semibold text-[var(--text-primary)] text-sm">معاينة مباشرة</h3>
          </div>
          <div className="p-5 space-y-4">
            {/* Sidebar preview */}
            <div>
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2 font-semibold">القائمة الجانبية</p>
              <div className="bg-[#080F1A] rounded-xl p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl grid place-items-center overflow-hidden shrink-0 shadow-[0_0_12px_rgba(0,78,235,0.4)]" style={{ background: form.primary_color }}>
                  {preview
                    ? <img src={preview} alt="logo" className="w-6 h-6 object-contain" />
                    : <JasrMark size={24} />
                  }
                </div>
                <div>
                  <div className="text-white font-black text-sm leading-tight" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                    {form.name || "اسم المنصة"}<span style={{ color: form.primary_color }}>.</span>
                  </div>
                  <div className="text-white/35 text-[9px] mt-0.5">{form.tagline || "الشعار النصي"}</div>
                </div>
              </div>
            </div>

            {/* Login badge preview */}
            <div>
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2 font-semibold">صفحة تسجيل الدخول</p>
              <div className="bg-[#060810] rounded-xl p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg grid place-items-center overflow-hidden shrink-0" style={{ background: form.primary_color }}>
                  {preview
                    ? <img src={preview} alt="logo" className="w-5 h-5 object-contain" />
                    : <JasrMark size={20} />
                  }
                </div>
                <div>
                  <div className="text-white font-black text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                    {form.name || "اسم المنصة"}<span style={{ color: form.primary_color }}>.</span>
                  </div>
                  <div className="text-white/30 text-[9px]">{form.tagline || "الشعار النصي"}</div>
                </div>
              </div>
            </div>

            {/* Button preview */}
            <div>
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2 font-semibold">الأزرار</p>
              <button
                className="w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-all"
                style={{ background: form.primary_color, boxShadow: `0 4px 14px ${form.primary_color}40` }}
              >
                ابدأ مجاناً الآن
              </button>
            </div>

            {/* Nav bar preview */}
            <div>
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2 font-semibold">الصفحة الرئيسية</p>
              <div className="bg-[#060810] rounded-xl px-3 py-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-md grid place-items-center overflow-hidden shrink-0" style={{ background: form.primary_color }}>
                  {preview
                    ? <img src={preview} alt="logo" className="w-4 h-4 object-contain" />
                    : <JasrMark size={14} />
                  }
                </div>
                <span className="text-white font-black text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                  {form.name || "اسم المنصة"}<span style={{ color: form.primary_color }}>.</span>
                </span>
                <span className="text-[9px] text-white/25 border rounded-full px-1.5 py-0.5 ms-auto" style={{ borderColor: `${form.primary_color}30`, color: `${form.primary_color}90` }}>Beta</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════ Merchants Tab ════════════════ */
const STATE_CONFIG = {
  active:      { label: "نشط",           bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  onboarding:  { label: "في الإعداد",     bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-500" },
  installed:   { label: "مُثبَّت",         bg: "bg-indigo-50",  text: "text-indigo-700",  dot: "bg-indigo-500" },
  suspended:   { label: "معلّق",          bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500" },
  uninstalled: { label: "مُلغى التثبيت",  bg: "bg-red-50",     text: "text-red-600",     dot: "bg-red-400" },
};

function StateBadge({ state }) {
  const c = STATE_CONFIG[state] || { label: state, bg: "bg-slate-100", text: "text-[var(--text-secondary)]", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function MerchantsTab() {
  const [stats, setStats]       = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("");
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        api.get("/admin/merchant-stats"),
        api.get(`/admin/merchants?page=${page}&limit=15${filter ? `&state=${filter}` : ""}`),
      ]);
      setStats(statsRes.data);
      setMerchants(listRes.data.merchants || []);
      setTotalPages(listRes.data.pages || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const kpis = stats ? [
    { label: "إجمالي التجار",    value: stats.totals.all,         icon: Store,       color: "text-indigo-600 bg-indigo-50",  border: "border-indigo-100" },
    { label: "نشطون الآن",       value: stats.totals.active,      icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50", border: "border-emerald-100" },
    { label: "تثبيت جديد (30 يوم)", value: stats.totals.new_30d,  icon: UserPlus,    color: "text-blue-600 bg-blue-50",       border: "border-blue-100" },
    { label: "تجارب تنتهي قريباً", value: stats.totals.trial_expiring, icon: Clock,  color: "text-amber-600 bg-amber-50",     border: "border-amber-100" },
    { label: "إلغاء (30 يوم)",   value: stats.totals.churned_30d, icon: TrendingDown, color: "text-red-500 bg-red-50",        border: "border-red-100" },
    { label: "في الإعداد",       value: stats.totals.onboarding,  icon: Zap,         color: "text-purple-600 bg-purple-50",   border: "border-purple-100" },
  ] : [];

  return (
    <div className="space-y-6">

      {/* KPI cards */}
      {loading && !stats ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl p-4 animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map(({ label, value, icon: Icon, color, border }) => (
            <div key={label} className={`bg-[var(--bg-card)] border ${border} rounded-2xl p-4 flex flex-col gap-2 hover:shadow-sm transition-shadow`}>
              <div className={`w-9 h-9 rounded-xl grid place-items-center ${color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="text-2xl font-black text-[var(--text-primary)]">{value ?? "—"}</div>
              <div className="text-xs text-[var(--text-secondary)] leading-tight">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-2 text-sm font-bold text-[var(--text-primary)]">
            <Store className="w-4 h-4 text-[var(--text-muted)]" />
            قائمة التجار
          </div>
          <div className="flex items-center gap-2">
            {/* State filter */}
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="text-xs border border-[var(--border-color)] rounded-lg px-3 py-1.5 bg-[var(--bg-card)] text-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[#004EEB]/30"
              data-testid="merchant-state-filter"
            >
              <option value="">جميع الحالات</option>
              {Object.entries(STATE_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <button
              onClick={load}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-[var(--text-secondary)] transition-colors"
              title="تحديث"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="merchants-table">
            <thead className="bg-[var(--bg-card-hover)]/60 text-xs text-[var(--text-secondary)] uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3 text-right font-semibold">المتجر</th>
                <th className="px-4 py-3 text-right font-semibold">البريد</th>
                <th className="px-4 py-3 text-right font-semibold">الحالة</th>
                <th className="px-4 py-3 text-right font-semibold">الخطة</th>
                <th className="px-4 py-3 text-right font-semibold">تاريخ التثبيت</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {merchants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-[var(--text-muted)] text-sm">
                    {loading ? "جاري التحميل..." : "لا يوجد تجار بعد"}
                  </td>
                </tr>
              ) : merchants.map((m) => (
                <tr key={m.id} className="hover:bg-[var(--bg-card-hover)]/60 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-[var(--text-primary)]">{m.store_name || "—"}</div>
                    {m.store_url && (
                      <div className="text-xs text-[var(--text-muted)] truncate max-w-[180px]">{m.store_url}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)] text-xs">{m.email || "—"}</td>
                  <td className="px-4 py-3"><StateBadge state={m.state} /></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      m.subscription?.plan === "trial"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700"
                    }`}>
                      {m.subscription?.plan === "trial" ? "تجريبي" : m.subscription?.plan || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)] dir-ltr">
                    {m.installed_at ? new Date(m.installed_at).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-color)]">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="text-xs px-3 py-1.5 border border-[var(--border-color)] rounded-lg disabled:opacity-40 hover:bg-[var(--bg-card-hover)] transition-colors"
            >
              السابق
            </button>
            <span className="text-xs text-[var(--text-secondary)]">صفحة {page} من {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="text-xs px-3 py-1.5 border border-[var(--border-color)] rounded-lg disabled:opacity-40 hover:bg-[var(--bg-card-hover)] transition-colors"
            >
              التالي
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ════════════════ Main Admin Dashboard ════════════════ */
export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("tenants");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-7">

        {/* Header */}
        <header className="fade-up flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">لوحة السوبر أدمن</p>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#004EEB]/10 text-[#004EEB] border border-[#004EEB]/20">
                <ShieldCheck className="w-2.5 h-2.5" /> Admin Only
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-1">
              لوحة إدارة المنصة
            </h1>
            <p className="text-[var(--text-secondary)] mt-1.5 text-sm">
              أدر المستأجرين وإعدادات المنصة من مكان واحد.
            </p>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-1 bg-[var(--bg-card)] border border-[var(--border-color)]/80 p-1 rounded-xl w-fit">
          {[
            { key: "tenants",   icon: Users,    label: "المستأجرون" },
            { key: "merchants", icon: Store,     label: "التجار" },
            { key: "platform",  icon: Settings2, label: "إعدادات المنصة" },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === key
                  ? "bg-[#004EEB] text-white shadow-sm"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === "tenants"   && <TenantsTab user={user} />}
        {activeTab === "merchants" && <MerchantsTab />}
        {activeTab === "platform"  && <PlatformSettingsTab />}

        {/* Brand watermark */}
        <div className="flex justify-center pt-4">
          <JasrBrand showTagline />
        </div>
      </div>
    </div>
  );
}
